import { useTranslation } from "react-i18next"

import { cn } from "@/shared/lib/utils"
import { formatNumber } from "@/shared/lib/staffing-shared"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"

import type { HourlyBreakdownTableProps } from "../types/hourly-breakdown"

export function HourlyBreakdownTableSkeleton() {
  return (
    <div className="overflow-visible rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] animate-pulse">
      <div className="bg-[var(--color-surface-muted)]/60 h-10" />
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-1.5">
          <div className="w-16 h-4 bg-[var(--color-border)]/60 rounded" />
          {Array.from({ length: 7 }).map((_, j) => (
            <div key={j} className="flex-1 h-8 bg-[var(--color-border)]/40 rounded" />
          ))}
        </div>
      ))}
    </div>
  )
}

export function HourlyBreakdownTable({ data, isLoading = false }: HourlyBreakdownTableProps) {
  const { t } = useTranslation()

  if (isLoading || !data) {
    return <HourlyBreakdownTableSkeleton />
  }

  return (
    <div className="overflow-visible rounded-xl bg-[var(--color-surface-muted)]/30">
      <div className="overflow-visible">
        <Table className="table-auto">
          <TableHeader className="bg-[var(--color-surface-muted)]/60">
            <TableRow className="hover:bg-transparent">
              <TableHead className="border-b border-r border-[var(--color-border)] px-2 py-1.5 body-base font-medium text-[var(--color-text-primary)] sticky left-0 bg-[var(--color-surface-muted)]/60 z-10 whitespace-nowrap">
                {t("hourlyBreakdown.time")}
              </TableHead>
              {data.days.map((day, index) => (
                <TableHead
                  key={day.dayKey}
                  className={cn(
                    "border-b border-r border-[var(--color-border)] px-2 py-1.5 text-center whitespace-nowrap",
                    index === data.days.length - 1 && "border-r-0",
                  )}
                >
                  <div className="body-base font-medium text-[var(--color-text-primary)]">
                    {t(day.dayKey)}
                  </div>
                  <div className="body-xs text-[var(--color-text-secondary)] font-normal">
                    {formatNumber(day.totalCalls)} {t("hourlyBreakdown.calls")}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.timeSlots.map((time, timeIndex) => {
              const isLastRow = timeIndex === data.timeSlots.length - 1
              const borderB = isLastRow ? "" : "border-b"

              return (
                <TableRow key={time} className="group hover:bg-[var(--color-surface-muted)]/50">
                  <TableCell
                    className={cn(
                      "border-r border-[var(--color-border)] px-2 py-1 body-sm text-[var(--color-text-primary)] sticky left-0 bg-[var(--color-surface-muted)]/30 group-hover:bg-[var(--color-surface-muted)]/50 z-10 transition-colors whitespace-nowrap",
                      borderB,
                    )}
                  >
                    {time}
                  </TableCell>
                  {data.days.map((day, dayIndex) => (
                    <TableCell
                      key={`${day.dayKey}-${time}`}
                      className={cn(
                        "border-r border-[var(--color-border)] px-2 py-1 text-center whitespace-nowrap",
                        dayIndex === data.days.length - 1 && "border-r-0",
                        borderB,
                      )}
                    >
                      <div className="body-sm text-[var(--color-text-primary)]">
                        {day.hours[timeIndex]?.agents} {t("hourlyBreakdown.agents")}
                      </div>
                      <div className="body-xs text-[var(--color-text-secondary)]">
                        {day.hours[timeIndex]?.calls} {t("hourlyBreakdown.calls")}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
